import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';

import Select from '../../../common2/Select';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import CarrierSelect from './CarrierSelect';
import DateInput from '../../../common2/DateInput';
import Locale from '../../../Translate/Locale';
import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import { useClaimCarrierSelectOptions } from './useClaimCarrierSelectOptions';
import Switch from '../../../Wizard/Switch';
import ClaimStepClosableMessage from './ClaimStepClosableMessage';
import useOnFormChange from '../../../../hooks/useOnFormChange';
import { getClaimableWarning, isClaimWithMissingTrackingData, isManualClaim } from '../claimsHelpers';
import Text from '../../../common/Text/Text';
import ExternalLink from '../../../common2/links/ExternalLink';
import Uploader from '../../../common/Uploader';
import InputHelpLink from '../InputHelpLink';
import { useValidateStep } from './hooks/useValidateStep';
import { useFlag } from '@unleash/proxy-client-react';
import Column from '../../../common/layout/Column';
import './yupClaimsExtensions';
import { ClaimContext, ClaimShopCarrier, ClaimType, ShipmentType } from '../types';

import styles from './DeadlineShippingInfoStep.scss';
import { ClaimStepContainerProps } from './ClaimStepContainer';

const shipmentTypeOptions = [
  { value: ShipmentType.OUTBOUND, label: 'Outbound' },
  { value: ShipmentType.RETURN, label: 'Return' },
  { value: ShipmentType.UNDELIVERABLE_OR_POSTAL_RETURN, label: 'Undeliverable / Postal Return' },
];

const schema = yup.object().shape({
  type: yup.string().required(),
  shipmentType: yup.string().required().label('Shipment type'),
  carrierCode: yup.string().required().label('Carrier'),
  carrierCountryCode: yup.string().required().label('Carrier country'),
  firstHubScanDate: yup
    .date()
    .nullable()
    .label('First hub scan date')
    .when(['type', '$isNon7sCarrier'], {
      is: (type: ClaimType, isNon7sCarrier: boolean) => type !== ClaimType.INVESTIGATION || !isNon7sCarrier,
      then: (schema) => schema.required(),
    }),
  postalReturnDate: yup
    .date()
    .nullable()
    .when(['type', 'shipmentType'], {
      is: (type: ClaimType, shipmentType: ShipmentType) =>
        type === ClaimType.INVESTIGATION && shipmentType === ShipmentType.UNDELIVERABLE_OR_POSTAL_RETURN,
      then: (schema) => schema.required(),
    })
    .label('Postal return date'),
  deliveryDate: yup
    .date()
    .nullable()
    .when(['type'], {
      is: (type: ClaimType) => type !== ClaimType.INVESTIGATION,
      then: (schema) => schema.required(),
    })
    .label('Delivery date'),
  postalReturnDeliveryDate: yup.date().nullable().label('Delivery date'),
  trackingScreenshot: yup
    .array()
    .when('$isTrackingScreenshotRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Tracking screenshot'),
});

export type DeadlineShippingInfoClaimContext = Pick<
  ClaimContext,
  | 'type'
  | 'shipmentType'
  | 'carrierCode'
  | 'carrierCountryCode'
  | 'submissionInfo'
  | 'shipmentId'
  | 'entryPoint'
  | 'carrier'
  | 'deliveryDate'
  | 'postalReturnDeliveryDate'
  | 'firstHubScanDate'
  | 'postalReturnDate'
  | 'trackingScreenshot'
  | 'isDelivered'
  | 'isReturnShipment'
>;

type Props = ClaimStepContainerProps & {
  shopId: number;
  claim: DeadlineShippingInfoClaimContext;
  carriers: ClaimShopCarrier[];
  as: React.ComponentType<ClaimStepContainerProps>;
  onChange: (data: Partial<ClaimContext>) => void;
};

function DeadlineShippingInfoStep(props: Props) {
  const { claim, carriers, as: Container, onNext, onChange, ...restProps } = props;
  const { type, shipmentType, carrierCode, carrierCountryCode } = claim;

  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);

  const isCashOnDeliveryClaim = type === 'cash_on_delivery';

  const { carrierOptions, carrierCountryOptions } = useClaimCarrierSelectOptions({
    carrierCode: claim.carrierCode,
    carrierCountryCode: claim.carrierCountryCode,
    carriers,
    type,
  });

  const onShipmentTypeChange = useCallback(
    (shipmentType: ShipmentType) =>
      onChange({
        shipmentType,
        isReturnShipment: shipmentType === ShipmentType.RETURN,
        isDelivered: false,
        // reset all dates when changing shipment type to avoid hidden dates which aren't relevant anymore
        firstHubScanDate: null,
        postalReturnDate: null,
        deliveryDate: null,
        postalReturnDeliveryDate: null,
        declarationOfRecipient: [], // if it was already filled on another step, it's not relevant anymore
      }),
    [onChange]
  );

  useEffect(() => {
    if (isCashOnDeliveryClaim) {
      onShipmentTypeChange(ShipmentType.OUTBOUND);
    }
  }, [isCashOnDeliveryClaim, onShipmentTypeChange]);

  const onInputChange = useOnFormChange(onChange);

  const onDeliveryDateChange = useCallback(
    (deliveryDate: Date | null) => onChange({ deliveryDate, isDelivered: !!deliveryDate }),
    [onChange]
  );

  const onPostalReturnDeliveryDateChange = useCallback(
    (postalReturnDeliveryDate: Date | null) =>
      onChange({ postalReturnDeliveryDate, isDelivered: !!postalReturnDeliveryDate }),
    [onChange]
  );

  const isClaimsCenterNewSubmissionFlowEnabled = useFlag('claims-center-new-submission-flow');
  const isParcelFinderNewSubmissionFlowEnabled = useFlag('parcel-finder-new-submission-flow');

  const isTrackingDataMissing = isClaimWithMissingTrackingData(claim);
  const isNewFlow =
    (claim.entryPoint === 'parcel-finder' && isParcelFinderNewSubmissionFlowEnabled) ||
    (claim.entryPoint === 'claims-center' && isClaimsCenterNewSubmissionFlowEnabled);

  const { isNon7s: isNon7sCarrier } = claim.carrier || {};

  const isTrackingScreenshotRequired =
    (isTrackingDataMissing || isManualClaim(claim)) && !!claim.carrier && !isNon7sCarrier && isNewFlow;

  const context = { isTrackingScreenshotRequired, isNon7sCarrier };

  const { isFieldRequired, errors, onClickNext, onClickSaveDraft } = useValidateStep(schema, claim, context, props);

  const claimableWarning = getClaimableWarning(claim);

  if (claimableWarning && claimableWarning !== 'no_required_tracking_data') {
    console.error(`Unexpected claimable warning "${claimableWarning}"`);
    return null;
  }

  const getDateInputProps = (name: keyof DeadlineShippingInfoClaimContext) => ({
    name,
    value: claim[name],
    error: errors[name],
    noErrorTranslation: true,
    isRequired: isFieldRequired(name, { value: claim, context }),
    onChange: onInputChange,
  });

  const selectedShipmentTypeAndFlow = `${
    type === 'investigation' ? 'investigation' : 'damage_or_missing_item'
  }_${shipmentType}`;

  const moreInformationLink =
    // eslint-disable-next-line max-len
    "https://support.portal.sevensenders.com/support/solutions/articles/15000030711-how-to-submit-a-claim#If-Seven-Senders'-tracking-is-incomplete ";

  return (
    <Container {...restProps} onSaveDraft={onClickSaveDraft} onNext={onClickNext} isDisabled={isNextButtonDisabled}>
      {isTrackingDataMissing && (
        <ClaimStepClosableMessage intent="warning">
          <div className={styles.inline}>
            <Text>
              <Locale>CLAIMS_MISSING_TRACKING_DATA</Locale>&nbsp;
            </Text>
            <ExternalLink to={moreInformationLink}>
              <Locale>LINK_FOR_MISSING_TRACKING_DATA_MORE_INFORMATION</Locale>
            </ExternalLink>
          </div>
        </ClaimStepClosableMessage>
      )}
      {!isTrackingDataMissing && (
        <ClaimStepClosableMessage intent="danger">
          <Locale>CLAIMS_NO_SHIPMENT_FOUND_ENTER_DEADLINE_INFO</Locale>{' '}
        </ClaimStepClosableMessage>
      )}
      <Column>
        <SidePanelContentSection heading="Deadline check">
          <ColumnsLayout>
            <Select
              label="Shipment type"
              placeholder="Select shipment type"
              value={shipmentType}
              options={shipmentTypeOptions}
              onChange={onShipmentTypeChange}
              error={errors.shipmentType}
              isDisabled={isCashOnDeliveryClaim || isTrackingDataMissing}
              isRequired
            />
            <div />
            <CarrierSelect
              carriers={carriers}
              carrierCode={carrierCode}
              carrierCountryCode={carrierCountryCode}
              carrierOptions={carrierOptions}
              carrierCountryOptions={carrierCountryOptions}
              errors={errors}
              isDisabled={isTrackingDataMissing}
              onChange={onChange}
            />
            <DateInput {...getDateInputProps('firstHubScanDate')} label="First hub scan date" />
            <Switch selected={selectedShipmentTypeAndFlow}>
              <DateInput
                key="investigation_outbound"
                {...getDateInputProps('deliveryDate')}
                label="Delivery date"
                onChange={onDeliveryDateChange}
              />
              <DateInput
                key="investigation_return"
                {...getDateInputProps('deliveryDate')}
                label="Delivery date at carrier's hub"
                onChange={onDeliveryDateChange}
              />
              <React.Fragment key="investigation_undeliverable_or_postal_return">
                <DateInput {...getDateInputProps('postalReturnDate')} label="Postal return date" />
                <DateInput
                  {...getDateInputProps('postalReturnDeliveryDate')}
                  label="Delivery date at carrier's hub"
                  onChange={onPostalReturnDeliveryDateChange}
                />
              </React.Fragment>
              <DateInput
                key="damage_or_missing_item_outbound"
                {...getDateInputProps('deliveryDate')}
                label="Delivery date"
                onChange={onDeliveryDateChange}
              />
              <DateInput
                key="damage_or_missing_item_return"
                {...getDateInputProps('deliveryDate')}
                label="Delivery date"
                helpText="WAREHOUSE_DELIVERY_AS_DELIVERY_DATE_HELP_TEXT"
                onChange={onDeliveryDateChange}
              />
              <DateInput
                key="damage_or_missing_item_undeliverable_or_postal_return"
                {...getDateInputProps('deliveryDate')}
                helpText="WAREHOUSE_DELIVERY_AS_DELIVERY_DATE_HELP_TEXT"
                label="Delivery date"
                onChange={onDeliveryDateChange}
              />
            </Switch>
          </ColumnsLayout>
        </SidePanelContentSection>
        {isTrackingScreenshotRequired && (
          <SidePanelContentSection heading="PROOF_OF_DELIVERY_EVENTS">
            <Uploader
              name="trackingScreenshot"
              label="Tracking screenshot"
              topRightElement={
                // eslint-disable-next-line max-len
                <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Tracking-Screen-Shot-Requirements%C2%A0">
                  Tracking screenshot requirements
                </InputHelpLink>
              }
              allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
              helpText="CLAIMS_SCREENSHOT_UPLOAD_HELP_TEXT"
              isRequired
              files={claim.trackingScreenshot}
              errorWithTranslation={errors.trackingScreenshot}
              onChange={onInputChange}
              onBusy={setIsNextButtonDisabled}
              maxNumberOfFiles={1}
            />
          </SidePanelContentSection>
        )}
      </Column>
    </Container>
  );
}

export default React.memo(DeadlineShippingInfoStep);
